import { ChartNoAxesColumnIcon, LibraryBigIcon, SearchIcon, SettingsIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { openCommandPalette } from "../../commandPaletteBus";
import { useEnvironmentIdentificationMode } from "../../hooks/useSettings";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { cn, isMacPlatform } from "../../lib/utils";
import {
  resolveEnvironmentIdentificationPillLabel,
  resolveSidebarStageBackdropVariant,
  SidebarStageBackdrop,
  useEnvironmentStageLabel,
} from "../SidebarStageBackdrop";
import { Badge } from "../ui/badge";
import {
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import { AMOREG_MARK_BODY_PATH, AMOREG_MARK_LEG_PATH } from "../AmoregBrandMark";
import { SidebarInboxButton } from "./SidebarInboxButton";
import { SidebarProviderUpdatePill } from "./SidebarProviderUpdatePill";
import { SidebarUpdatePill } from "./SidebarUpdatePill";

export const SidebarChromeHeader = memo(function SidebarChromeHeader({
  isElectron,
}: {
  isElectron: boolean;
}) {
  const stageLabel = useEnvironmentStageLabel();
  const environmentIdentificationMode = useEnvironmentIdentificationMode();
  const backdropVariant = resolveSidebarStageBackdropVariant(
    stageLabel,
    environmentIdentificationMode === "artwork",
  );
  const pillLabel =
    environmentIdentificationMode === "pill"
      ? resolveEnvironmentIdentificationPillLabel(stageLabel)
      : null;

  return (
    <SidebarHeader
      className={cn(
        // From md the shell's top strip carries the brand and the company
        // (ShellTopBar), so this header — brand, stage pill and the mobile
        // trigger — is a mobile-only row.
        "@container/sidebar-header relative h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center px-3 py-0 md:hidden",
        isElectron && "drag-region",
      )}
    >
      {backdropVariant ? <SidebarStageBackdrop variant={backdropVariant} /> : null}
      <SidebarTrigger
        className={cn(
          "relative z-10 md:hidden",
          backdropVariant &&
            "[:hover,[data-pressed]]:bg-white/15 focus-visible:ring-white/90 focus-visible:ring-offset-blue-700 [&_svg]:stroke-white/90! [&_svg]:opacity-100! [&_svg]:hover:stroke-white!",
        )}
      />
      <SidebarBrand onBackdrop={backdropVariant !== null} isElectron={isElectron} />
      {pillLabel ? (
        <Badge
          className="relative z-10 ml-1 rounded-full px-1.5 text-muted-foreground"
          data-environment-identification="pill"
          size="sm"
          variant="secondary"
        >
          {pillLabel}
        </Badge>
      ) : null}
      {/* Mobile keeps inbox access while the primary rail is hidden. */}
      <div className="relative z-10 ml-1.5 flex items-center md:hidden">
        <SidebarInboxButton onBackdrop={backdropVariant !== null} />
      </div>
    </SidebarHeader>
  );
});

function SidebarBrand({ onBackdrop, isElectron }: { onBackdrop: boolean; isElectron: boolean }) {
  // The titlebar offset only exists to clear native macOS traffic lights;
  // with the nav rail owning the floating toggle, everywhere else the brand
  // sits at the sidebar's normal content inset.
  const clearsTrafficLights = isElectron && isMacPlatform(navigator.platform);
  return (
    <Link
      aria-label="Go to threads"
      className={cn(
        "sidebar-brand relative z-10 h-7 w-fit min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-md outline-hidden ring-ring focus-visible:ring-2",
        clearsTrafficLights
          ? "ml-[var(--workspace-titlebar-content-left)]"
          : // Aligns the mark with the org picker's content below it: group
            // inset + the row's own content padding.
            "ml-[calc(var(--sidebar-content-inset)+var(--sidebar-row-content-inset)-1px)] max-md:ml-1",
        onBackdrop ? "text-white" : "text-foreground",
      )}
      to="/"
    >
      <AmoregMark />
      <span
        className={cn(
          "truncate text-sm font-medium tracking-tight",
          onBackdrop ? "text-white/70" : "text-muted-foreground",
        )}
      >
        Amoreg
      </span>
    </Link>
  );
}

export { AMOREG_MARK_BODY_PATH, AMOREG_MARK_LEG_PATH } from "../AmoregBrandMark";

/** The Amoreg mark, in the current text colour. */
export function AmoregMark() {
  return (
    <svg
      aria-label="Amoreg"
      className="h-3.5 w-auto shrink-0"
      viewBox="0 0 73 74"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={AMOREG_MARK_LEG_PATH} fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d={AMOREG_MARK_BODY_PATH} fill="currentColor" />
    </svg>
  );
}

export const SidebarChromeFooter = memo(function SidebarChromeFooter() {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const handleSettingsClick = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    void navigate({ to: "/settings" });
  }, [isMobile, navigate, setOpenMobile]);

  const regulationActiveOrgId = useActiveOrgId();
  const regulationOrgSegment = regulationActiveOrgId ? orgUrlSegment(regulationActiveOrgId) : null;

  const handleUsageClick = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    // Tenant-scoped when we know the org; the legacy route redirects otherwise.
    if (regulationOrgSegment) {
      void navigate({ to: "/$org/usage", params: { org: regulationOrgSegment } });
      return;
    }
    void navigate({ to: "/usage" });
  }, [isMobile, navigate, regulationOrgSegment, setOpenMobile]);

  const handleLibraryClick = useCallback(() => {
    setOpenMobile(false);
    // Tenant-scoped when we know the org; the legacy route redirects otherwise.
    if (regulationOrgSegment) {
      void navigate({
        to: "/$org/compliance/policies",
        params: { org: regulationOrgSegment },
      });
      return;
    }
    void navigate({ to: "/compliance" });
  }, [navigate, regulationOrgSegment, setOpenMobile]);

  const handleSearchClick = useCallback(() => {
    // No delay, unlike HelpMenu: the closing Sheet returns focus only if it is
    // still inside the Sheet or on the body once its exit ends, and the
    // palette's input has it by then. Only a click on the palette's own
    // backdrop dismisses it.
    setOpenMobile(false);
    openCommandPalette();
  }, [setOpenMobile]);

  // On desktop only an update fills it, so without one it takes no room.
  return (
    <SidebarFooter className="px-[var(--sidebar-content-inset)] py-1 empty:hidden" data-sidebar-chrome-footer="">
      <SidebarProviderUpdatePill />
      <SidebarUpdatePill />
      {/* The rail reaches Search, Governance and Settings from md; below md it is hidden. */}
      {isMobile ? (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSearchClick}>
              <SearchIcon />
              <span>Search</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLibraryClick}>
              <LibraryBigIcon />
              <span>Governance</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleUsageClick}>
              <ChartNoAxesColumnIcon />
              <span>Usage</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSettingsClick}>
              <SettingsIcon />
              <span>Settings</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      ) : null}
    </SidebarFooter>
  );
});
